// Builds the PUT v2/seasons/... body for one season FROM ITS DETAIL
// (GET v2/seasons/..., which carries season.sensors).
//
// - active / archived: the season (V1's field projection) is parsed exactly as
//   V1 parses a field (GeneralClasses.Field), the changed values are applied the
//   way UpdateFieldModal's handlers apply them, buildFieldForSave runs as on V1
//   submit, and fieldManager.build_update_field_body produces the same body V1's
//   update_field PUTs. The server runs V1's update_field_logic on it.
// - template: only the changed keys (partial data), merged onto the template's
//   own crop_data / irrigation_system, and no sensors. Advanced-section edits
//   (Field paths such as 'crop_data.kc_table') are sent as V1 would send them:
//   the V1 body of the template with every change applied supplies them.
// The sensors are the ones Edit Field would send: the sensor objects the
// sensors section edited (sensorEditRules.js — added, changed, deleted,
// Tal Gil sensors linked to the farming unit's), which replace the Field's
// parsed ones as UpdateFieldModal's handleSensorsChange does;
// build_update_field_body turns them back with sensorObjectsToDictionary.
// buildSeasonPutBody refuses a body that lost any of them.
const GeneralClasses = require('GeneralClasses');
const fieldManager = require('fieldManager');
const { shouldWeekStartWithMonday } = require('farmingUnitManager');
const { buildFieldForSave } = require('../dashboard/field/editFieldSaveGuards');
const {
  buildSeasonPutBody,
  activationContractDates,
  CROP_KEYS,
  IRRIGATION_KEYS,
  SETTINGS_KEYS,
  isFieldPath,
  setPath,
} = require('./seasonHelpers');
const { cloneSensors } = require('./sensorEditRules');

// UpdateFieldModal's default week start: the farming unit's country, else
// the distribution's name (shouldWeekStartWithMonday with both, as V1 calls
// it). 0 = Monday, 6 = Sunday.
const firstDayOfWeekFor = (farmingUnit, distributionName) =>
  shouldWeekStartWithMonday(farmingUnit, distributionName) ? 0 : 6;

const applyChange = (field, key, value) => {
  if (CROP_KEYS.includes(key)) {
    field.crop_data = { ...field.crop_data, [key]: value };
  } else if (key === 'irrigation_type') {
    field.irrigation_system = {
      ...field.irrigation_system,
      irrigation_type: value,
      recommendation_display: fieldManager.defaultRecommendationDisplay(value),
    };
  } else if (IRRIGATION_KEYS.includes(key)) {
    field.irrigation_system = { ...field.irrigation_system, [key]: value };
  } else if (SETTINGS_KEYS.includes(key)) {
    field.settings = { ...field.settings, [key]: value };
  } else if (key === 'cluster') {
    // buildFieldForSave reads the id back as Edit Field's cluster select.
    field.cluster = { ...field.cluster, id: value };
  } else if (key === 'area_size') {
    field.geo_data = { ...field.geo_data, area_size: value };
  } else if (key === 'expiration_date') {
    field.contract_data = { ...field.contract_data, expiration_date: value };
  } else if (isFieldPath(key)) {
    // Advanced sections: the value Edit Field's handler sets at that path.
    setPath(field, key, value);
  } else {
    field[key] = value;
  }
};

// The season parsed exactly as V1 parses a field. A pre-season may have no
// `settings` yet, which V1's parser reads unguarded.
const parseSeasonField = (season, entity) =>
  new GeneralClasses.Field(
    JSON.parse(JSON.stringify({ ...season, settings: season.settings || {} })),
    season.id,
    firstDayOfWeekFor(entity.farmingUnit, entity.distributionName),
  );

// A copy of the parsed field with the changes applied as Edit Field's handlers
// apply them (what the Advance section's components show while editing).
const withChanges = (field, changes) => {
  const copy = Object.assign(Object.create(Object.getPrototypeOf(field)), {
    ...field,
  });
  Object.keys(changes).forEach((key) => applyChange(copy, key, changes[key]));
  return copy;
};

const buildV1Body = (season, changes, entity, sensors) => {
  const field = parseSeasonField(season, entity);
  Object.keys(changes).forEach((key) => applyChange(field, key, changes[key]));
  if (sensors) {
    field.sensors = cloneSensors(sensors);
  }
  const forSave = buildFieldForSave({
    currentField: field,
    clusterId: field.cluster?.id ?? '',
    soilType: field.soil_type,
  });
  return fieldManager.build_update_field_body(forSave);
};

// entity: {farmingUnit, distributionName} — the V2 farming unit and its
// distribution's name, for the default week start (as V1 picks it).
// sensors: the season's sensor objects to save (useSeasonSensors).
const buildSeasonSaveBody = (detailSeason, changes, entity, sensors) =>
  buildSeasonPutBody(
    detailSeason,
    changes,
    {
      buildV1Body: (season, seasonChanges, seasonSensors) =>
        buildV1Body(season, seasonChanges, entity, seasonSensors),
      defaultRecommendationDisplay: fieldManager.defaultRecommendationDisplay,
    },
    sensors,
  );

// "Activate now": V1's create-field request for a pre-season, from its DETAIL
// (GET v2/seasons/...): parsed as V1 parses a field, made an active season
// installed today, with no sensors (the new season starts without them), and
// built by fieldManager.build_create_field_body — the body V1's Add Field
// POSTs. The server runs V1's create_field_logic on it.
const buildActivationBody = (detailSeason, entity, nowUnix) => {
  const field = parseSeasonField(detailSeason, entity);
  field.sensors = {};
  field.settings = { ...field.settings, field_state: 'active' };
  field.contract_data = {
    ...field.contract_data,
    ...activationContractDates(field.contract_data, nowUnix),
  };
  const forSave = buildFieldForSave({
    currentField: field,
    clusterId: field.cluster?.id ?? '',
    soilType: field.soil_type,
  });
  return fieldManager.build_create_field_body(forSave);
};

module.exports = {
  buildSeasonSaveBody,
  buildActivationBody,
  parseSeasonField,
  withChanges,
};
